import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type {
  FetchFormEntriesRequest,
  FetchFormEntriesResponse,
  AddSectionToLibraryRequest,
  AddSectionToLibraryResponse,
  GetEntitesFromLibraryRequest,
  GetEntitesFromLibraryResponse,
  DeleteSectionsFromLibraryRequest,
  DeleteSectionsFromLibraryResponse,
  FormUserSettingsControllerRequest,
  FormUserSettingsControllerResponse,
  TaskInspectionAndProgressFormsControllerRequest,
  TaskInspectionAndProgressFormsControllerResponse,
  FormFlowControllerRequest,
  FormFlowControllerResponse,
  GetTemplatesResponse,
  GetTemplateTypesResponse,
  TemplateDefaultRequest,
  SaveTemplateRequest,
  EditTemplateRequest,
  DeleteTemplateRequest,
  ArchiveTemplateRequest,
  UpdateUsedTemplateRequest,
  TemplateTypesDefaultRequest,
  InsertTemplateTypeRequest,
  UpdateTemplateTypeRequest,
  DeleteTemplateTypeRequest,
} from "@ServerTypes/forms/forms.schema";
// Runtime request schemas — the SAME Zod schemas the server enforces on req.body via
// registerRoutes/validateRequest (single source of truth). Imported as runtime values (not `type`)
// so each dedicated action fail-fast validates (.parse throws) its payload before the network call.
// Because the server gates every request on these schemas, any payload it would accept already
// conforms here, so this cannot reject a currently-valid flow.
import {
  templateDefaultSchema,
  saveTemplateSchema,
  editTemplateSchema,
  deleteTemplateSchema,
  archiveTemplateSchema,
  updateUsedTemplateSchema,
  templateTypesDefaultSchema,
  insertTemplateTypeSchema,
  updateTemplateTypeSchema,
  deleteTemplateTypeSchema,
} from "@ServerTypes/forms/forms.schema";

// Each former action-dispatcher is split into dedicated endpoints. These maps route an `action` to
// its endpoint path; an unmapped/absent action falls back to the list endpoint.
const TASK_INSPECTION_ACTION_PATHS: Record<string, string> = {
  update: "/forms/updateTaskInspectionAndProgress",
  save: "/forms/saveTaskInspectionAndProgress",
  delete: "/forms/deleteTaskInspectionAndProgress",
  archive: "/forms/archiveTaskInspectionAndProgress",
};
const FORM_USER_SETTINGS_ACTION_PATHS: Record<string, string> = {
  createForm: "/forms/createFormFromUserSettings",
  deleteFormUserSettings: "/forms/deleteFormUserSettings",
  checkFormUserSettings: "/forms/checkFormUserSettings",
};
const FORM_FLOW_ACTION_PATHS: Record<string, string> = {
  update: "/forms/updateFormFlow",
  save: "/forms/saveFormFlow",
  delete: "/forms/deleteFormFlow",
  duplicate: "/forms/duplicateFormFlow",
  addToLibrary: "/forms/addFormFlowToLibrary",
  addFromLibrary: "/forms/addFormFlowFromLibrary",
  archive: "/forms/archiveFormFlow",
};
// Task-inspection `action` may be at the top level or nested under `value` (matching the old controller).
export async function taskInspectionAndProgressFormsController<
  T extends Exact<TaskInspectionAndProgressFormsControllerRequest, T>,
>(data: T) {
  const d = data as { action?: string; value?: { action?: string } };
  const action = d.action ?? d.value?.action;
  const path = (action && TASK_INSPECTION_ACTION_PATHS[action]) || "/forms/taskInspectionAndProgressFormsController";
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}${path}`,
    data,
  );
  return response;
}
export async function formUserSettingsController<T extends Exact<FormUserSettingsControllerRequest, T>>(data: T) {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_USER_SETTINGS_ACTION_PATHS[action]) || "/forms/checkFormUserSettings";
  const response = await axiosWrapper.post<FormUserSettingsControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}

export async function fetchFormEntries<T extends Exact<FetchFormEntriesRequest, T>>(data: T) {
  const response = await axiosWrapper.post<FetchFormEntriesResponse>(`${getBackendUrl()}/forms/fetchFormEntries`, data);
  return response;
}
export async function formFlowController<T extends Exact<FormFlowControllerRequest, T>>(data: T) {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_FLOW_ACTION_PATHS[action]) || "/forms/formFlowController";
  const response = await axiosWrapper.post<FormFlowControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}
// ── Templates: one dedicated function per endpoint (no generic dispatcher). Each fail-fast validates
//    its payload against the server request schema, then posts to its own dedicated endpoint. ──
export const getTemplates = async <T extends Exact<TemplateDefaultRequest, T>>(data: T) => {
  templateDefaultSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/getTemplates`, data);
};
export const saveTemplate = async <T extends Exact<SaveTemplateRequest, T>>(data: T) => {
  saveTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/saveTemplate`, data);
};
export const editTemplate = async <T extends Exact<EditTemplateRequest, T>>(data: T) => {
  editTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/editTemplate`, data);
};
export const deleteTemplate = async <T extends Exact<DeleteTemplateRequest, T>>(data: T) => {
  deleteTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/deleteTemplate`, data);
};
export const archiveTemplate = async <T extends Exact<ArchiveTemplateRequest, T>>(data: T) => {
  archiveTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/archiveTemplate`, data);
};
export const updateUsedTemplate = async <T extends Exact<UpdateUsedTemplateRequest, T>>(data: T) => {
  updateUsedTemplateSchema.parse(data);
  return axiosWrapper.post<GetTemplatesResponse>(`${getBackendUrl()}/forms/updateUsedTemplate`, data);
};

// ── Template types: one dedicated function per endpoint. ──
export const getTemplateTypes = async <T extends Exact<TemplateTypesDefaultRequest, T>>(data: T) => {
  templateTypesDefaultSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/getTemplateTypes`, data);
};
export const insertTemplateType = async <T extends Exact<InsertTemplateTypeRequest, T>>(data: T) => {
  insertTemplateTypeSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/insertTemplateType`, data);
};
export const updateTemplateType = async <T extends Exact<UpdateTemplateTypeRequest, T>>(data: T) => {
  updateTemplateTypeSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/updateTemplateType`, data);
};
export const deleteTemplateType = async <T extends Exact<DeleteTemplateTypeRequest, T>>(data: T) => {
  deleteTemplateTypeSchema.parse(data);
  return axiosWrapper.post<GetTemplateTypesResponse>(`${getBackendUrl()}/forms/deleteTemplateType`, data);
};

export const addSectionToLibrary = async <T extends Exact<AddSectionToLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<AddSectionToLibraryResponse>(
    `${getBackendUrl()}/forms/addSectionToLibrary`,
    data,
  );
  return response;
};
export const getEntitesFromLibrary = async <T extends Exact<GetEntitesFromLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetEntitesFromLibraryResponse>(
    `${getBackendUrl()}/forms/getEntitesFromLibrary`,
    data,
  );
  return response;
};

export const deleteSectionsFromLibrary = async <T extends Exact<DeleteSectionsFromLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<DeleteSectionsFromLibraryResponse>(
    `${getBackendUrl()}/forms/deleteSectionsFromLibrary`,
    data,
  );
  return response;
};
